import React from 'react';

/**
 * ExamResult
 *
 * Renders the final exam result. Three main states:
 *   - PASS (score >= 75 AND every section >= 12)
 *   - FAIL_SECTION_MIN (score >= 75 but at least one section < 12)
 *   - FAIL_TOTAL (score < 75)
 *
 * Pass state celebrates and points to Account Settings for the certificate.
 * Fail states give specific feedback per section and a retry path.
 * If they hit cooldown (3 consecutive fails), they get the cooldown message
 * and the retry button is disabled.
 *
 * Expected result payload from scoreExam Cloud Function:
 * {
 *   passed: boolean,
 *   totalScore: number,
 *   sectionScores: { rice: number, workflow: number, roi: number, agent: number },
 *   sectionFeedback: { rice: string, workflow: string, roi: string, agent: string },
 *   strongestSection: 'rice' | 'workflow' | 'roi' | 'agent',
 *   weakestSection: 'rice' | 'workflow' | 'roi' | 'agent',
 *   failedSectionMins: ['rice', ...] | [],
 *   overallNote: string,
 *   cooldownTriggered: boolean,
 *   cooldownHoursRemaining: number,
 *   consecutiveFails: number,
 * }
 */

const SECTION_LABELS = {
  rice: 'Role-Specific RICE Prompt',
  workflow: 'Workflow Redesign',
  roi: 'ROI Measurement',
  agent: 'Role-Specific Agent',
};

const SECTION_NUMBERS = {
  rice: '01',
  workflow: '02',
  roi: '03',
  agent: '04',
};

export default function ExamResult({ result, user, onNav, onRetake }) {
  const {
    passed,
    totalScore = 0,
    sectionScores = {},
    sectionFeedback = {},
    strongestSection,
    weakestSection,
    failedSectionMins = [],
    overallNote = '',
    cooldownTriggered = false,
    cooldownHoursRemaining = 12,
    consecutiveFails = 0,
  } = result;

  const sectionsArr = ['rice', 'workflow', 'roi', 'agent'];

  // Determine result type
  let resultType;
  if (passed) {
    resultType = totalScore >= 90 ? 'pass_strong' : 'pass_squeak';
  } else if (totalScore >= 75 && failedSectionMins.length > 0) {
    resultType = 'fail_section_min';
  } else {
    resultType = 'fail_total';
  }

  return (
    <div style={s.wrapper}>
      {/* ============ HEADER ============ */}
      <div style={{
        ...s.header,
        background: passed ? '#1a8a4a' : '#1B2A4A',
      }}>
        <div style={s.dayTag}>
          {passed ? 'Final Exam · Passed' : 'Final Exam · Not Yet'}
        </div>
        <div style={s.title}>
          {resultType === 'pass_strong' && 'You passed.'}
          {resultType === 'pass_squeak' && 'You passed.'}
          {resultType === 'fail_section_min' && 'Tough one. So close.'}
          {resultType === 'fail_total' && 'Not this time.'}
        </div>
      </div>

      {/* ============ SCORE HERO ============ */}
      <div style={s.scoreHero}>
        <div style={s.scoreCircle}>
          <div style={s.scoreNum}>{totalScore}</div>
          <div style={s.scoreDenom}>of 100</div>
        </div>
        <div style={s.scoreRight}>
          <div style={s.scoreLabel}>
            {passed ? 'Total Score' : `Total Score · Need 75 to pass`}
          </div>
          <div style={s.scoreMsg}>
            {resultType === 'pass_strong' && getPassStrongMessage(totalScore, strongestSection)}
            {resultType === 'pass_squeak' && getPassSqueakMessage(totalScore, weakestSection)}
            {resultType === 'fail_section_min' && getSectionMinMessage(totalScore, failedSectionMins)}
            {resultType === 'fail_total' && getFailTotalMessage(totalScore, strongestSection, weakestSection)}
          </div>
        </div>
      </div>

      {/* ============ SECTION BREAKDOWN ============ */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Section Breakdown</div>
        <div style={s.breakdownGrid}>
          {sectionsArr.map(secId => {
            const score = sectionScores[secId] || 0;
            const failed = failedSectionMins.includes(secId);
            const isStrongest = secId === strongestSection;
            const isWeakest = secId === weakestSection && !passed;
            return (
              <div key={secId} style={{
                ...s.breakdownCard,
                borderLeftColor: failed ? '#C0392B' : score >= 20 ? '#1a8a4a' : score >= 12 ? '#D4A847' : '#C0392B',
              }}>
                <div style={s.bdNumber}>{SECTION_NUMBERS[secId]}</div>
                <div style={s.bdLabel}>{SECTION_LABELS[secId]}</div>
                <div style={s.bdScoreRow}>
                  <span style={s.bdScore}>{score}</span>
                  <span style={s.bdOf}>/25</span>
                  {failed && <span style={s.bdFailTag}>Below 12</span>}
                  {isStrongest && passed && <span style={s.bdStrongTag}>Strongest</span>}
                </div>
                <div style={s.bdBar}>
                  <div style={{
                    ...s.bdFill,
                    width: `${(score / 25) * 100}%`,
                    background: failed ? '#C0392B' : score >= 20 ? '#1a8a4a' : score >= 12 ? '#D4A847' : '#C0392B',
                  }}></div>
                </div>
                {sectionFeedback[secId] && (
                  <div style={s.bdFeedback}>{sectionFeedback[secId]}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ OVERALL FEEDBACK ============ */}
      {overallNote && (
        <div style={s.overallPanel}>
          <div style={s.overallLabel}>Overall</div>
          <div style={s.overallText}>{overallNote}</div>
        </div>
      )}

      {/* ============ CTA / NEXT STEPS ============ */}
      {passed && (
        <div style={s.passCTA}>
          <div style={s.passCTAHeadline}>Your certificate is in Account Settings</div>
          <div style={s.passCTAText}>
            Head to your profile section to download it. Save it, share it, print it for your wall. You earned it.
          </div>
          <div style={s.btnRow}>
            <button style={s.primaryBtn} onClick={() => onNav('account')}>
              Get My Certificate →
            </button>
            <button style={s.secondaryBtn} onClick={() => onNav('dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}

      {!passed && cooldownTriggered && (
        <div style={s.cooldownCTA}>
          <div style={s.cooldownIcon}>⏳</div>
          <div style={s.cooldownHeadline}>That is three in a row. Cooldown time.</div>
          <div style={s.cooldownText}>
            The exam is locked for the next {cooldownHoursRemaining} {cooldownHoursRemaining === 1 ? 'hour' : 'hours'} so you can step away and come back fresh. Open your workbook. Look at the prompt library you built, the workflow you redesigned, the math you ran, the agent you specced. The answers are in there. You are closer than this score is making you feel.
          </div>
          <div style={s.btnRow}>
            <button style={s.secondaryBtn} onClick={() => onNav('workbooks')}>
              Open My Workbooks
            </button>
            <button style={s.secondaryBtn} onClick={() => onNav('dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}

      {!passed && !cooldownTriggered && (
        <div style={s.failCTA}>
          <div style={s.failHeadline}>
            {resultType === 'fail_section_min'
              ? `Tighten up the section${failedSectionMins.length > 1 ? 's' : ''} below 12 and you are there.`
              : 'Go back to your work. The answers are in your workbooks.'}
          </div>
          <div style={s.failText}>
            Hit the books, my friend. Try again tomorrow, or whenever you are ready.
            {consecutiveFails > 0 && (
              <span style={s.failCount}> Attempt streak: {consecutiveFails} failed in a row. Three in a row triggers a 12 hour cooldown.</span>
            )}
          </div>
          <div style={s.btnRow}>
            <button style={s.primaryBtn} onClick={onRetake}>
              Try Again
            </button>
            <button style={s.secondaryBtn} onClick={() => onNav('workbooks')}>
              Review My Workbooks
            </button>
            <button style={s.secondaryBtn} onClick={() => onNav('dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ MESSAGE BUILDERS ============

function getPassStrongMessage(score, strongestSection) {
  return `${score} out of 100. Real talk, your ${SECTION_LABELS[strongestSection] || 'work'} answer was the strongest of the four. That is the kind of thinking that takes a training program and turns it into actual ROI for your team. Nicely done. Now go put it to work.`;
}

function getPassSqueakMessage(score, weakestSection) {
  return `${score} out of 100. Squeaked it but a pass is a pass. One note for the road, your ${SECTION_LABELS[weakestSection] || 'weakest'} answer was the thinnest of the four. As you keep using this stuff at work, get tighter there. It is the difference between people nodding politely and people opening their checkbooks.`;
}

function getSectionMinMessage(score, failedSections) {
  const failedLabels = failedSections.map(s => SECTION_LABELS[s]).join(' and ');
  return `You landed at ${score} out of 100 which would have passed, but ${failedSections.length > 1 ? 'those sections' : 'that section'} (${failedLabels}) scored under 12 and we need at least 12 on every section to call it earned. The rest of your work was solid. Tighten ${failedSections.length > 1 ? 'those up' : 'that one up'} and the certificate is yours.`;
}

function getFailTotalMessage(score, strongestSection, weakestSection) {
  return `You landed at ${score} out of 100. The good news, nothing is broken that cannot be fixed. Your ${SECTION_LABELS[weakestSection] || 'weakest'} answer needs the most work. Your ${SECTION_LABELS[strongestSection] || 'strongest'} answer was closest to where you need all four to be. Use that one as the bar.`;
}

const s = {
  wrapper: { maxWidth: '880px', paddingBottom: '60px', fontFamily: 'DM Sans, sans-serif' },

  header: { padding: '28px 32px' },
  dayTag: { fontSize: '10px', fontWeight: '600', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '6px' },
  title: { fontFamily: 'Playfair Display, serif', fontSize: '28px', color: '#fff', lineHeight: '1.2' },

  scoreHero: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '32px', display: 'flex', alignItems: 'center', gap: '32px' },
  scoreCircle: {
    width: '140px',
    height: '140px',
    borderRadius: '50%',
    background: '#1B2A4A',
    border: '4px solid #D4A847',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  scoreNum: { fontFamily: 'Playfair Display, serif', fontSize: '52px', fontWeight: '900', color: '#fff', lineHeight: '1' },
  scoreDenom: { fontSize: '11px', color: '#D4A847', letterSpacing: '1.5px', textTransform: 'uppercase', marginTop: '4px' },
  scoreRight: { flex: 1 },
  scoreLabel: { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '10px', fontWeight: '600' },
  scoreMsg: { fontFamily: 'Playfair Display, serif', fontSize: '16px', color: '#1B2A4A', lineHeight: '1.6', fontWeight: '400' },

  section: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '28px 32px' },
  sectionTitle: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1B2A4A', marginBottom: '18px', paddingBottom: '8px', borderBottom: '2px solid #E8E6E2' },

  breakdownGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  breakdownCard: { background: '#FAF8F5', borderLeft: '4px solid #E8E6E2', padding: '18px 20px', position: 'relative' },
  bdNumber: { fontFamily: 'Playfair Display, serif', fontSize: '18px', fontWeight: '700', color: '#D4A847', marginBottom: '4px' },
  bdLabel: { fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', color: '#1B2A4A', marginBottom: '10px' },
  bdScoreRow: { display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' },
  bdScore: { fontFamily: 'Playfair Display, serif', fontSize: '32px', fontWeight: '900', color: '#1B2A4A', lineHeight: '1' },
  bdOf: { fontSize: '13px', color: '#8A8A8A' },
  bdFailTag: { fontSize: '9px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', background: '#C0392B', color: '#fff', padding: '3px 8px', marginLeft: 'auto' },
  bdStrongTag: { fontSize: '9px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', background: '#1a8a4a', color: '#fff', padding: '3px 8px', marginLeft: 'auto' },
  bdBar: { height: '6px', background: '#E8E6E2', marginBottom: '10px', overflow: 'hidden' },
  bdFill: { height: '100%', transition: 'width 0.6s ease-out' },
  bdFeedback: { fontSize: '12px', color: '#3D3D3D', lineHeight: '1.5', fontWeight: '300', fontStyle: 'italic' },

  overallPanel: { background: '#1B2A4A', padding: '24px 32px' },
  overallLabel: { fontSize: '10px', letterSpacing: '2px', textTransform: 'uppercase', color: '#D4A847', marginBottom: '8px', fontWeight: '600' },
  overallText: { fontSize: '14px', color: 'rgba(255,255,255,0.85)', lineHeight: '1.7', fontWeight: '300' },

  passCTA: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '32px', textAlign: 'center' },
  passCTAHeadline: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '10px' },
  passCTAText: { fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px', maxWidth: '540px', margin: '0 auto 24px' },

  failCTA: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '32px', textAlign: 'center' },
  failHeadline: { fontFamily: 'Playfair Display, serif', fontSize: '18px', color: '#1B2A4A', marginBottom: '10px' },
  failText: { fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px', maxWidth: '560px', margin: '0 auto 24px' },
  failCount: { display: 'block', marginTop: '6px', fontSize: '12px', color: '#8A6F2A', fontStyle: 'italic' },

  cooldownCTA: { background: '#FFFFFF', border: '1.5px solid #E8E6E2', borderTop: 'none', padding: '40px 32px', textAlign: 'center' },
  cooldownIcon: { fontSize: '52px', marginBottom: '12px' },
  cooldownHeadline: { fontFamily: 'Playfair Display, serif', fontSize: '20px', color: '#1B2A4A', marginBottom: '12px' },
  cooldownText: { fontSize: '14px', color: '#3D3D3D', lineHeight: '1.7', marginBottom: '24px', maxWidth: '600px', margin: '0 auto 24px' },

  btnRow: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  primaryBtn: { background: '#1B2A4A', color: '#fff', border: 'none', padding: '13px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  secondaryBtn: { background: 'transparent', color: '#1B2A4A', border: '1.5px solid #1B2A4A', padding: '13px 28px', fontFamily: 'Playfair Display, serif', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
};
